import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
  Signal,
  signal,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import { Store } from '@ngrx/store';

import { OrderAttachmentsComponent } from '@components/orders/manage-orders/order-attachments/order-attachments.component';

import { singleOrderDetails } from '../../../../@store/orders/orders.model';
import { selectIsOrdersManager } from '../../../../@store/user/user.selectors';

@Component({
  selector: 'ciwc-order-job-details',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    OrderAttachmentsComponent,
  ],
  standalone: true,
  templateUrl: './order-job-details.component.html',
  styleUrl: './order-job-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderJobDetailsComponent implements OnInit {
  private readonly store = inject(Store);
  readonly isOrdersManager = this.store.selectSignal(selectIsOrdersManager);

  @Input({ required: true }) set form(value: FormGroup) {
    this._form = value;
    if (!this._form.get('attachments')) {
      this._form.addControl('attachments', this.fb.array([]));
    }
  }
  get form(): FormGroup {
    return this._form;
  }

  @Input() setFormValues: Signal<boolean> = signal(false);

  @Input() set orderDetails(value: singleOrderDetails | null) {
    if (this.setFormValues()) return;

    this.orderDetailsSignal.set(value);
    if (value?.attachments) {
      this.setAttachments(value.attachments);
    }
    // Patch form fields with orderDetails values
    if (value && this._form) {
      const lastShipment =
        Array.isArray(value.shipments) && value.shipments.length > 0
          ? value.shipments[value.shipments.length - 1]
          : null;

      this._form.patchValue({
        clientPurchaseOrder: value.clientPurchaseOrder ?? '',
        pricePerUnit: value.pricePerUnit ?? null,
        orderTotalPrice: value.orderTotalPrice ?? null,
        totalQuantity: value.totalQuantity ?? null,
        balanceToFollow: value.balanceToFollow ?? null,
        overage: value.overage ?? null,
        weeksAmount: value.weeksAmount ?? '',
        promisedDate: value.dueDate ? new Date(value.dueDate) : null,
        expeditedOrder: value.expeditedOrder ?? false,
        expediteDate: value.expediteDate ? new Date(value.expediteDate) : null,
        expeditePrice: value.expeditePrice ?? null,
        faiReport: value.faiReport ?? false,
        faiReportPrice: value.faiReportPrice ?? null,
        lastShipmentAmount: lastShipment ? (lastShipment.totalPrice ?? null) : null,
        testReports: value.testReports ?? false,
        certsRequired: value.certsRequired ?? false,
        cfm: value.cfm ?? false,
        ciwToSupplyWire: value.ciwToSupplyWire ?? false,
        isComplete: value.isComplete ?? false,
        shippingDetailsText: value.shippingDetailsText ?? '',
      });
    }
  }

  private orderDetailsSignal = signal<singleOrderDetails | null>(null);
  private fb = inject(FormBuilder);
  private _form!: FormGroup;

  get priceLabel(): string {
    const unit = this.orderDetailsSignal()?.unit;
    return unit === 'EA' ? 'Price Each' : 'Price per Foot';
  }

  get priceSuffix(): string {
    const unit = this.orderDetailsSignal()?.unit;
    return unit === 'EA' ? '/ EA' : '/ 1ft';
  }

  get attachments(): FormArray {
    return this.form.get('attachments') as FormArray;
  }

  weeksOptions = [
    { value: '', label: 'None' },
    ...Array.from({ length: 52 }, (_, i) => ({
      value: i + 1,
      label: `${i + 1} Week${i + 1 > 1 ? 's' : ''}`,
    })),
  ];
  @Input() viewDisabled!: boolean;

  trackByValue(index: number, item: any) {
    return item.value;
  }

  onWeeksAmountChange(event: any) {
    // handle weeks amount change if needed
  }

  private setAttachments(attachments: any[] = []) {
    const newArray = this.fb.array<FormGroup<any>>([]);
    (attachments || []).forEach((att) => {
      newArray.push(
        this.fb.group({
          name: [{ value: att.attachmentName || att.name, disabled: this.viewDisabled }],
          document: [
            { value: att.attachmentDocument || att.document, disabled: this.viewDisabled },
          ],
          url: [att.url],
          isUploading: [false],
          uploadError: [''],
        }) as FormGroup<any>,
      );
    });
    this.form.setControl('attachments', newArray);
  }

  ngOnInit(): void {
    // Subscribe to expeditedOrder changes to enable/disable expediteDate
    if (this._form) {
      const expeditedOrderControl = this._form.get('expeditedOrder');
      const expediteDateControl = this._form.get('expediteDate');
      const promisedDateControl = this._form.get('promisedDate');
      const weeksAmountControl = this._form.get('weeksAmount');

      if (expeditedOrderControl && expediteDateControl) {
        expeditedOrderControl.valueChanges.subscribe((checked: boolean) => {
          if (checked) {
            expediteDateControl.enable();
          } else {
            expediteDateControl.disable();
            expediteDateControl.setValue(null);
          }
          this.recalculateOrderTotalPrice();
        });
        // Initial state
        if (!expeditedOrderControl.value) {
          expediteDateControl.disable();
        }
      }

      promisedDateControl?.valueChanges.subscribe(() => {
        weeksAmountControl?.setValue('');
      });

      // Auto-calculate orderTotalPrice based on unit, sections, and extras
      const recalcTriggers = [
        'pricePerUnit',
        'totalQuantity',
        'expeditedOrder',
        'expeditePrice',
        'faiReport',
        'faiReportPrice',
      ];
      recalcTriggers.forEach((key) => {
        const ctrl = this._form.get(key);
        ctrl?.valueChanges.subscribe(() => this.recalculateOrderTotalPrice());
      });

      // Initial calculation on init
      this.recalculateOrderTotalPrice();
    }
  }

  private recalculateOrderTotalPrice(): void {
    if (!this._form) return;

    const details = this.orderDetailsSignal();
    const unit = details?.unit;
    const sections = Number(details?.sections || 0);

    const pricePerUnit = Number(this._form.get('pricePerUnit')?.value || 0);
    const totalQuantity = Number(this._form.get('totalQuantity')?.value || 0);
    const expeditedOrder = !!this._form.get('expeditedOrder')?.value;
    const expeditePrice = Number(this._form.get('expeditePrice')?.value || 0);
    const faiReport = !!this._form.get('faiReport')?.value;
    const faiReportPrice = Number(this._form.get('faiReportPrice')?.value || 0);

    const base =
      unit === 'EA'
        ? Number((sections * pricePerUnit).toFixed(2))
        : Number((totalQuantity * pricePerUnit).toFixed(2));
    const extras = (expeditedOrder ? expeditePrice : 0) + (faiReport ? faiReportPrice : 0);

    const total = Number.isFinite(base + extras) ? Number((base + extras).toFixed(2)) : 0;

    const orderTotalCtrl = this._form.get('orderTotalPrice');
    orderTotalCtrl?.setValue(total, { emitEvent: false });
  }
}
